import { inject, Injectable, ModelSignal } from '@angular/core'
import { catchError, from, lastValueFrom, Observable, of, tap } from 'rxjs'
import { APIEnvelope } from '../shared/models/api'
import { FilterParams, PaginationParams, SortParams } from '../shared/utilities/http-params'
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http'
import { GLOBAL_LOADING_SPINNER_ENABLED } from '../shared/interceptors/loading-spinner-interceptor'
import { format } from 'date-fns'
import { est } from '../shared/utilities/date'
import { IntervalUsage } from '../shared/models/intervalUsage'
import { DownloadService } from '../shared/services/download.service'
import { ToastService } from '../public/toasts/toasts.service'
import { IdrColumnDataType } from './idr-columns'
import { union } from 'lodash'
import { IdrExportArgs } from './idr-export-modal.component'

export type IdrResult = Readonly<Record<string, string | boolean | number>>

type WideIntervaleFilters = Readonly<Record<string, string>>

export type WideIntervalExportBody = Readonly<{
  columns: readonly string[],
  filters: WideIntervaleFilters
  order_by: readonly string[]
}>

const automaticFilters: WideIntervaleFilters = {
  channel_code: '-1',
  meter_type_code: 'I',
}

export type WideIntervalQueryBody = WideIntervalExportBody & Readonly<{
  limit: number
  start?: number
}>

type IdrColumnConfig = Readonly<{
  data_type: IdrColumnDataType
  friendly_name: string
}>


export type IdrColumnsResponse = Readonly<{
  table_columns: Readonly<Record<string, IdrColumnConfig>>,
  search_columns: readonly string[]
}>

const getFiltersFromParams = (filters: readonly FilterParams[]): WideIntervaleFilters => {
  const result: Record<string, string> = { ...automaticFilters }
  filters.forEach(({ name, values }) => {
    result[name] = values.map(({ operation, value }) =>
      `${operation && operation !== '==' ? operation : ''}${value}`,
    ).join(',')
  })
  return result
}

const getOrderByColumns = (order: readonly SortParams[]): readonly string[] =>
  order.map(({ name, direction }) => `${direction === 'desc' ? '-' : ''}${name}`)

const hasNewColumns = (oldColumns: readonly string[], newColumns: readonly string[])=>
  !newColumns.every(_ => oldColumns.includes(_))

type ResultCache = Readonly<{
  columns: readonly string[]
  fingerprint: string
  value: APIEnvelope<IdrResult>
}>

const getFingerprint = ({ filters, order_by, start, limit }: WideIntervalQueryBody): string =>
  JSON.stringify({ filters, order_by, start, limit })

const initialCache: ResultCache = {
  columns: [],
  fingerprint: '',
  value: { total: 0, results: [] },
}

@Injectable({ providedIn: 'root' })
export class IdrInvestigationService {
  private readonly http = inject(HttpClient)
  private readonly downloadService = inject(DownloadService)
  private readonly toast = inject(ToastService)
  private resultCache: ResultCache = initialCache

  private handleError = ({ error, status }: HttpErrorResponse) => {
    this.toast.showError(error, undefined, status)
    return of({ total: 0, results: [] as IntervalUsage[], error: true })
  }

  private async getResultsPromise (query: WideIntervalQueryBody): Promise<APIEnvelope<IdrResult>> {
    const fingerprint = getFingerprint(query)
    if (this.resultCache.fingerprint !== fingerprint || hasNewColumns(this.resultCache.columns, query.columns)) {
      // query conditions/sorting/paging changed, or we selected more columns to display then previous result
      const httpResult = await lastValueFrom(
        this.http.post<APIEnvelope<IdrResult>>('wide-interval/query', query),
      )
      this.resultCache = { columns: query.columns, fingerprint, value: { ...httpResult, cached: true } }
      return httpResult
    } else {
      return this.resultCache.value
    }
  }

  getResults (
    columns: readonly string[],
    filters: readonly FilterParams[],
    order: readonly SortParams[],
    { pageNumber, pageSize }: PaginationParams,
  ): Observable<APIEnvelope<IdrResult>> {
    return from(this.getResultsPromise({
      columns: union(Object.keys(automaticFilters), columns),
      filters: getFiltersFromParams(filters),
      limit: pageSize,
      order_by: getOrderByColumns(order),
      start: pageSize * (pageNumber - 1) + 1,
    }))
  }

  getColumns (): Observable<IdrColumnsResponse> {
    return this.http.get<IdrColumnsResponse>('wide-interval/columns')
  }

  exportResults({ columns, filters, order }: IdrExportArgs, cancelled: ModelSignal<boolean>): Observable<unknown> {
    const body: WideIntervalExportBody = {
      columns: union(Object.keys(automaticFilters), columns),
      filters: getFiltersFromParams(filters),
      order_by: getOrderByColumns(order),
    }
    return this.http
      .post<string[]>('wide-interval/export', body, {
        context: new HttpContext().set(GLOBAL_LOADING_SPINNER_ENABLED, false),
      })
      .pipe(
        tap((urls) => {
          if (!cancelled()) {
            const timestamp = format(est(), 't')
            void this.downloadService.download(
              urls.map((url, i) => ({
                url,
                name: `idr-investigation-${i}-${timestamp}.csv`,
              })),
            )
          }
        }),
        catchError(this.handleError),
      )
  }

  clearCache () {
    this.resultCache = initialCache
  }
}
